import i18next from "i18next";
import { EnemyManager } from "../handlers/enemy-handler";
import { Player } from "../models/player";
import { WindowManager } from "../ui/window-manager";
import { MapHandler, TELEPORT_TILE_INDEX } from "../handlers/map-handler";
import {
  AttackAnimationManager,
  AttackEventData,
  spawnBloodSplatter,
} from "../handlers/attack-handler";
import {
  spawnPlayerLabel,
  updateDamageTexts,
  clearAllDamageTexts,
} from "../ui/damage-text";
import { playSound } from "../utils/sound-utils";
import {
  showGraySplashParticle,
  showSplashParticle,
  showBeamUpParticle,
  showDeathBloodSplash,
} from "../ui/splash-particle";
import { preloadPhaserAssets, SpriteLoader } from "../sprite-loader";
import {
  registerInventoryAndPlayerListeners,
  unregisterInventoryAndPlayerListeners,
  registerLootBoxListeners,
  unregisterLootBoxListeners,
  registerDisconnectHandler,
  unregisterDisconnectHandler,
  startConnectionQualityMonitoring,
  stopConnectionQualityMonitoring,
  registerItemListeners,
  unregisterItemListeners,
  registerPlayerListeners,
  unregisterPlayerListeners,
} from "../network";
import { LootBox, CHEST_LOCK_DURATION_MS } from "../models/loot-box";
import { setupTouchControls } from "../controls/controls-touch";
import {
  setupDesktopControls,
  resetDesktopControls,
} from "../controls/controls-desktop";
import { DamageFlash } from "../ui/damage-flash";
import { MeleeAttackSpriteManager } from "../ui/melee-attack-sprite";
import { setSmartCameraBounds } from "../utils/camera-utils";
import { MatchEndData } from "../types/match-end-data";
import { ThrowableHandler } from "../handlers/throwable-handler";
import { setThrowableMode } from "../controls/controls-touch";
import { isMobile, isTablet, isTouchDevice } from "../utils/mobile-utils";
import { isAndroid } from "../utils/android-utils";
import { Enemy } from "../models/enemy";
import { AudioManager } from "../managers/audio-manager";
import { COLORS } from "../ui/colors";
import { BadConnectionIndicator } from "../ui/bad-connection-indicator";
import { trackEvent } from "../utils/ga-events";
import {
  Item,
  SlotType,
  PLAYER_UNARMED_ATTACK_RANGE,
  TILE_SIZE,
  LIMBS_DEFAULT_VALUES,
  PlayerData,
  LootBoxSpriteInfo,
  EnemyState,
  getLootBoxSpriteInfo,
  LootBoxInfo,
  ItemDefinition,
} from "../models/common";
import { getLootBagSprite } from "../utils/sprite-utils";
import { BaseGameScene } from "./base-game-scene";
import { updateLootBoxHighlighting } from "../utils/scene-utils";
import { tutorialHandler } from "../handlers/tutorial-handler";
import { createCloudEffect, CloudEffect } from "../effects/cloud-effect";
import { createFireflyEffect } from "../effects/firefly-effect";
import { KillMessage, KillerInfo } from "../ui/kill-message";
import { StickerManager } from "../handlers/sticker-handler";

declare global {
  interface Window {
    allItems: any;
  }
}

const AIR_STRIKE_WARNING_DURATION_MS: number = 30000; // 30 seconds

export class MainScene extends BaseGameScene {
  /**
   * Last movement direction for the player (used for movement logic).
   */
  private lastMovementDirection: { x: number; y: number } = { x: 0, y: 0 };

  /**
   * Timestamp of the last movement input.
   */
  private lastMovementTime: number = 0;
  /**
   * Holds references to air strike warning UI elements for cleanup.
   */
  private _airStrikeWarningUI?: {
    timerText: Phaser.GameObjects.Text;
    warningText: Phaser.GameObjects.Text;
    takeCoverText: Phaser.GameObjects.Text;
    crosshair: Phaser.GameObjects.Image;
    crosshairUpdateInterval: ReturnType<typeof setInterval>;
    timerInterval: ReturnType<typeof setInterval>;
  };
  firstEnemyHighlight?: Phaser.GameObjects.Graphics;
  playerTileText?: Phaser.GameObjects.Text;
  lastFirstEnemyTile?: { x: number; y: number };
  socket: any;
  cursors: Phaser.Types.Input.Keyboard.CursorKeys | null;
  keys: {
    W: Phaser.Input.Keyboard.Key;
    A: Phaser.Input.Keyboard.Key;
    S: Phaser.Input.Keyboard.Key;
    D: Phaser.Input.Keyboard.Key;
  } | null;
  inputSeq: number;
  pendingInputs: Array<{ seq: number; dx: number; dy: number }>;
  initialPlayers: { [id: string]: { id: string; x: number; y: number } };
  initialStateReceived: boolean;
  enemyManager!: EnemyManager;
  damageFlash!: DamageFlash;
  meleeAttackSpriteManager!: MeleeAttackSpriteManager;
  throwableHandler: ThrowableHandler;
  private activeThrowableItem: Item | null = null;
  audioManager!: AudioManager;

  // Match timer properties
  matchTimerText?: Phaser.GameObjects.Text;
  matchTimeRemaining: number = 0;
  matchTimerEvent?: Phaser.Time.TimerEvent;

  // Chest lock countdown, updated once per second until all chests unlock
  private chestLockEvent?: Phaser.Time.TimerEvent;

  // Player death state
  playerDead: boolean = false;

  // Admin mode activation
  private adminKeySequence: string[] = ["a", "d", "m", "i", "n"];
  private currentKeySequence: string[] = [];
  private sequenceResetTimeout: ReturnType<typeof setTimeout> | null = null;

  // Debug mode: Press 'H' to toggle enemy hitbox visualization
  private debugHitboxes: boolean = false;

  // Camera zoom based on weapon zoom property
  private baseZoom: number = 1.1; // Default zoom for desktop
  private currentZoom: number = 1.1;
  private targetZoom: number = 1.1;

  bloodSplatters: Array<{
    graphics: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;
    createdAt: number;
  }> = [];

  attackAnimationManager!: AttackAnimationManager;
  stickerManager!: StickerManager;

  firstConnect: boolean;
  // Position update tracking
  positionUpdateInterval?: Phaser.Time.TimerEvent;

  // Reconnecting spinner overlay
  private reconnectingOverlay?: Phaser.GameObjects.Container;
  private reconnectingSpinner?: Phaser.GameObjects.Graphics;
  private reconnectingText?: Phaser.GameObjects.Text;

  // Ambient cloud drift effect
  private cloudEffect?: CloudEffect;

  // Ambient firefly effect
  private fireflyEmitter?: Phaser.GameObjects.Particles.ParticleEmitter;

  // Bad connection indicator
  private badConnectionIndicator?: BadConnectionIndicator;

  // Reconnection data
  private isReconnecting: boolean = false;
  private reconnectionData?: {
    position: { x: number; y: number };
    health: number;
    maxHealth: number;
    armor?: number;
    maxArmor?: number;
    matchTimeRemaining?: number;
  };
  // Squad code to identify squad members
  private mySquadCode?: string;

  // Match start time for duration tracking
  private matchStartTime?: number;

  constructor() {
    super("MainScene");
    this.socket = window.socket;
    this.cursors = null;
    this.keys = null;
    this.inputSeq = 0;
    this.pendingInputs = [];
    this.initialPlayers = {};
    this.initialStateReceived = false;
    this.firstConnect = true;

    this.attackAnimationManager = new AttackAnimationManager(this);
    this.stickerManager = new StickerManager(this);
    this.meleeAttackSpriteManager = new MeleeAttackSpriteManager(this);
    this.throwableHandler = new ThrowableHandler(this);
  }

  init(data?: { isReconnecting?: boolean; reconnectionData?: any }) {
    // Store reconnection state
    this.isReconnecting = data?.isReconnecting || false;
    this.reconnectionData = data?.reconnectionData;
  }

    preload() {
    // Centralized Phaser asset loading
    preloadPhaserAssets(this);

    // Load all items for use in projectile reconstruction
    try {
      // @ts-ignore - require is valid in this context
      const { loadAllItemsToWindow } = require("../utils/load-items");
      loadAllItemsToWindow();
    } catch (e) {
      console.warn("Could not load allItems for throwable projectiles:", e);
    }

    // Get selected map from window or default to forest
    const selectedMap = (window as any).selectedMap || "forest";
    const mapJSONPath = `/maps/${selectedMap}.json`;
    const mapKey = `map_${selectedMap}`;

    // Clear the tilemap cache for this specific map to force reload
    if (this.cache.tilemap.exists(mapKey)) {
      this.cache.tilemap.remove(mapKey);
    }

    // MapHandler preload (updated for multiple tilesets)
    this.mapHandler = new MapHandler(this, {
      mapKey: mapKey,
      mapJSONPath: mapJSONPath,
      tilesets: [
        {
          name: "key-objects",
          imageKey: "key-objects",
          imagePath: "/sprites/map/key-objects.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "house",
          imageKey: "house",
          imagePath: "/sprites/map/house.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-shelter",
          imageKey: "furniture-shelter",
          imagePath: "/sprites/map/furniture-shelter.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-toxic",
          imageKey: "furniture-toxic",
          imagePath: "/sprites/map/furniture-toxic.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-office",
          imageKey: "furniture-office",
          imagePath: "/sprites/map/furniture-office.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-fancy",
          imageKey: "furniture-fancy",
          imagePath: "/sprites/map/furniture-fancy.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "furniture-messy",
          imageKey: "furniture-messy",
          imagePath: "/sprites/map/furniture-messy.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "sand",
          imageKey: "sand",
          imagePath: "/sprites/map/sand.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "grass",
          imageKey: "grass",
          imagePath: "/sprites/map/grass.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "plants",
          imageKey: "plants",
          imagePath: "/sprites/map/plants.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "Mansion Furtniture",
          imageKey: "furniture-hotel",
          imagePath: "/sprites/map/furniture-hotel.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "graveyard",
          imageKey: "graveyard",
          imagePath: "/sprites/map/graveyard.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
        {
          name: "Lakes",
          imageKey: "lakes",
          imagePath: "/sprites/map/lakes.png",
          extruded: true,
          margin: 1,
          spacing: 2,
        },
      ],
    });
    this.mapHandler.preload();

    // Preload WindowManager icon assets
    WindowManager.preload(this);

    // Load weapon attack sounds
    this.load.audio("weapon-pistol", "/sfx/weapons/weapon-pistol.ogg");
    this.load.audio("weapon-shotgun", "/sfx/weapons/weapon-shotgun.ogg");
    this.load.audio("weapon-smg", "/sfx/weapons/weapon-smg.ogg");
    this.load.audio("weapon-rifle", "/sfx/weapons/weapon-rifle.ogg");
    this.load.audio("weapon-minigun", "/sfx/weapons/weapon-minigun.ogg");
    this.load.audio("weapon-reload", "/sfx/weapons/weapon-reload.ogg");

    this.load.audio("bandage", "/sfx/items/bandage.ogg");
    this.load.audio("heal", "/sfx/items/heal.ogg");

    this.load.audio("explosion", "/sfx/weapons/explosion.ogg");
    this.load.audio("no-ammo-sound", "/sfx/weapons/no-ammo.ogg");
    this.load.audio("unarmed-attack", "/sfx/weapons/unarmed-attack.ogg");
    this.load.audio("throw", "/sfx/weapons/throw.ogg");
    this.load.audio("explosion", "/sfx/weapons/explosion.ogg");
    // Note: highlight sound is loaded globally in loading-scene
    this.load.audio("hit", "/sfx/hit.ogg");

    // Load enemy SFX
    this.load.audio("enemy-creepy", "/sfx/enemies/creepy.ogg");
    this.load.audio("enemy-goo-boom", "/sfx/enemies/goo-boom.ogg");
    this.load.audio("enemy-scavenger", "/sfx/enemies/scavenger.ogg");
    this.load.audio("enemy-mutant-lord", "/sfx/enemies/mutant-lord.ogg");

    this.load.audio("teleport", "/sfx/teleport.ogg");
    this.load.audio("teleport-appear", "/sfx/teleport-appear.ogg");
    this.load.audio("airdrop", "/sfx/airdrop.ogg");
    this.load.audio("alarm", "/sfx/alarm.ogg");

    // Preload dust spritesheet for sprint effect
    this.load.spritesheet("dust", "/sprites/effects/dust.png", {
      frameWidth: 16,
      frameHeight: 16,
    });

    // Preload audio manager assets
    AudioManager.preload(this);
  }

  create() {
    // Track match start event
    trackEvent("enter-main");
    this.matchStartTime = Date.now();

    // --- UI Layer and WindowManager ---
    if (this.windowManager) {
      this.windowManager.destroy();
      this.windowManager = undefined;
    }
    this.setupUILayer();

    // Set a fallback background color
    this.cameras.main.setBackgroundColor("#222");
    // Remove camera zoom for UI consistency
    this.cameras.main.setZoom(1);
    // Enable pixel rounding to prevent tile seams
    this.cameras.main.roundPixels = true;

    // Create map using MapHandler
    this.mapHandler?.create();

    // Instantiate EnemyManager
    this.enemyManager = new EnemyManager(this);

    // Initialize damage flash effect
    this.damageFlash = new DamageFlash(this);

    // Initialize throwable handler
    this.throwableHandler = new ThrowableHandler(this);

    // Initialize and start audio manager
    this.audioManager = new AudioManager(this);
    this.audioManager.start();

    // Debug: Player tile position text (commented out)
    /*
    const width = this.cameras.main.width;
    this.playerTileText = this.add
      .text(width / 2, 8, "Player Tile: (0, 0)", {
        fontSize: "18px",
        color: "#fff",
        fontFamily: "Anton",
        align: "center",
        stroke: "#222",
        strokeThickness: 2,
      })
      .setOrigin(0.5, 0);
    this.playerTileText.setScrollFactor(0);
    this.playerTileText.setDepth(100001);

    // Add to UI layer so it's always visible
    this.uiLayer.add(this.playerTileText);
    */

    // Enemy update listener
    this.socket.on("enemy_update", (enemies: EnemyState[]) => {
      this.enemyManager.updateEnemies(enemies);
    });

    // Remove previous player and otherPlayers sprites
    if (this.player) {
      this.player.destroy();
    }
    Object.values(this.otherPlayers).forEach((op: Player) => {
      op.destroy();
    });
    this.player = undefined;
    this.otherPlayers = {};

    // Create players from initial state if received
    if (this.initialStateReceived) {
      Object.values(this.initialPlayers).forEach(
        (p: {
          id: string;
          x: number;
          y: number;
          weapon?: any;
          activeSkin?: string;
          equippedHat?: number;
        }) => {
          if (p.id === this.socket.id) {
            // Always use reconnectionData.position if available
            let px = p.x;
            let py = p.y;
            if (this.reconnectionData?.position) {
              px = this.reconnectionData.position.x;
              py = this.reconnectionData.position.y;
            }
            this.player = new Player(p.id, this, px, py, 100, 100);

            // Apply active skin - use server data if provided, fallback to previous player data if not
            if ("activeSkin" in p) {
              // Server explicitly sent activeSkin (could be undefined for default skin)
              this.player.data.activeSkin = p.activeSkin;
              this.player.updateSkin(p.activeSkin);
            } else if (window.player?.data?.activeSkin) {
              // Fallback only if server didn't provide activeSkin
              this.player.data.activeSkin = window.player.data.activeSkin;
              this.player.updateSkin(window.player.data.activeSkin);
            }

            // Apply weapon - use server data if provided, fallback to previous player data if not
            const inheritedWeapon1 = p.weapon ?? window.player?.data?.equipment?.Weapon;
            if (inheritedWeapon1) {
              this.player.data.equipment.Weapon = inheritedWeapon1;
              this.player.updateWeaponSprite();
            }

            // Apply equipped hat - use server data if provided, fallback to previous player data if not
            this.player.data.equippedHat = "equippedHat" in p ? p.equippedHat : window.player?.data?.equippedHat;
            this.player.updateHatSprite();

            // Update global reference to new player (replaces old player from base scene)
            window.player = this.player;

            // Set up callbacks to close windows on movement and damage
            this.player.setOnMoveCallback(() => {
              if (this.windowManager) {
                this.windowManager.closeAllWindows();
              }
            });
            this.player.setOnDamageCallback(() => {
              if (this.damageFlash) {
                this.damageFlash.flash();
              }
            });
            // Update camera zoom based on initial equipment
            this.updateCameraZoomForWeapon();
            // Show beam up effect when player first appears
            showBeamUpParticle(
              this,
              this.player.container.x,
              this.player.container.y,
            );
          } else {
            this.otherPlayers[p.id] = new Player(
              p.id,
              this,
              p.x,
              p.y,
              100,
              100,
              true,
            );
            // Apply active skin if provided
            if (p.activeSkin) {
              this.otherPlayers[p.id].data.activeSkin = p.activeSkin;
              this.otherPlayers[p.id].updateSkin(p.activeSkin);
            }
            // Apply equipped hat if provided
            this.otherPlayers[p.id].data.equippedHat = p.equippedHat;
            this.otherPlayers[p.id].updateHatSprite();
            // Make sure UI camera ignores other players
            if (this.uiCamera && this.otherPlayers[p.id].container) {
              this.uiCamera.ignore(this.otherPlayers[p.id].container);
            }
          }
        },
      );
      // Re-initialize controls after player is created
      if (this.player) {
        if (isTouchDevice()) {
          setupTouchControls(this);
        } else {
          setupDesktopControls(this);
        }
      }
    } else {
      // Fallback: create local player at off-screen position if initial state not received
      // This player will be repositioned when server state arrives
      if (!this.player) {
        const defaultX = -1000; // Off-screen position to avoid flash
        const defaultY = -1000;
        this.player = new Player(
          this.socket.id ?? "",
          this,
          defaultX,
          defaultY,
          100,
          100,
        );
        // Apply active skin if available from window.player data
        if (window.player?.data?.activeSkin) {
          this.player.data.activeSkin = window.player.data.activeSkin;
          this.player.updateSkin(window.player.data.activeSkin);
        }
        // Apply weapon if available from window.player data (player-data may have arrived already)
        if (window.player?.data?.equipment?.Weapon) {
          this.player.data.equipment.Weapon = window.player.data.equipment.Weapon;
          this.player.updateWeaponSprite();
        }
        // Apply equipped hat if available from window.player data
        if (window.player?.data?.equippedHat) {
          this.player.data.equippedHat = window.player.data.equippedHat;
          this.player.updateHatSprite();
        }
        // Make player invisible initially until proper position is received
        this.player.container.setVisible(false);
        window.player = this.player;
        // Set up callbacks to close windows on movement and damage
        this.player.setOnMoveCallback(() => {
          if (this.windowManager) {
            this.windowManager.closeAllWindows();
          }
        });
        this.player.setOnDamageCallback(() => {
          if (this.damageFlash) {
            this.damageFlash.flash();
          }
        });
        // Re-initialize controls after player is created
        if (isTouchDevice()) {
          setupTouchControls(this);
        } else {
          setupDesktopControls(this);
        }
        // Update camera zoom based on initial equipment
        this.updateCameraZoomForWeapon();
      }
    }

    // Make camera follow the player with smooth interpolation
    // Using moderate lerp for smooth camera while maintaining pixel art crispness
    if (this.player && this.player.container) {
      this.cameras.main.startFollow(this.player.container, true, 0.08, 0.08);

      // Automatically set camera bounds based on map dimensions with fallback
      const fallbackBounds = { x: 0, y: 0, width: 6144, height: 6144 }; // 192x192 tiles * 32px
      setSmartCameraBounds(
        this.cameras.main,
        this.mapHandler?.map || null,
        fallbackBounds,
      );
    }

    // Configure main camera: ignore UI layer and set zoom (adjusted for aspect ratio)
    if (this.uiLayer) {
      this.cameras.main.ignore(this.uiLayer);
    }
    const baseZoomValue = isTablet() ? 2.2 : isMobile() ? 1.5 : 1.1;
    let androidZoom = baseZoomValue;
    if (isAndroid()) {
      androidZoom *= 0.6; // Adjust this factor as needed for best results
    }
    this.baseZoom = androidZoom;
    this.currentZoom = this.baseZoom;
    this.targetZoom = this.baseZoom;
    this.cameras.main.setZoom(this.baseZoom);

    // Create UI camera for UI elements only
    this.uiCamera = this.cameras.add(0, 0, this.scale.width, this.scale.height);
    this.uiCamera.setScroll(0, 0);
    this.uiCamera.setBackgroundColor("rgba(0, 0, 0, 0)"); // Transparent background
    this.uiCamera.roundPixels = true;

    // Make UI camera ignore all existing game objects except uiLayer
    // Note: New objects created after this point need to be explicitly ignored
    // or they will be rendered by both cameras
    this.children.each((child: Phaser.GameObjects.GameObject) => {
      if (child !== this.uiLayer) {
        this.uiCamera!.ignore(child);
      }
    });

    // Ensure 'tile-object-overlay' layer is rendered above players and monsters
    const overlayLayer = this.mapHandler?.getLayer("tile-object-overlay");
    if (overlayLayer) {
      // Set high depth to render above all dynamic objects (1000 + max Y)
      overlayLayer.setDepth(10000);
    }

    // Ensure 'roofs' layer is rendered above overlay
    const roofsLayer = this.mapHandler?.getLayer("roofs");
    if (roofsLayer) {
      // Set even higher depth to render above overlay
      roofsLayer.setDepth(10001);
    }

    // Show UI buttons in upper right corner
    this.windowManager!.showUIButtons(this);
    // Hide credits label in MainScene
    this.windowManager!.hidePlayerWidget();
    // Show weapon UI in upper left corner
    this.windowManager!.showWeaponUI();

    // Create ambient cloud drift effect
    this.cloudEffect = createCloudEffect(this);

    // Create ambient firefly effect
    const mapW = this.mapHandler?.getMapWidthTiles() ?? 192;
    const mapH = this.mapHandler?.getMapHeightTiles() ?? 192;
    this.fireflyEmitter = createFireflyEffect(this, mapW, mapH);

    // --- Desktop crosshair cursor setup ---
    if (!isTouchDevice()) {
      // Always set default cursor to crosshair at scene start
      this.input.setDefaultCursor("crosshair");

      // Helper to set cursor style
      const setCursor = (style: string) => {
        this.input.manager.canvas.style.cursor = style;
      };

      // Listen for pointerover on UI layer to set default cursor
      if (this.uiLayer) {
        this.uiLayer.iterate((child: Phaser.GameObjects.GameObject) => {
          if (child.input && child.input.enabled) {
            child.on("pointerover", () => setCursor("default"));
            child.on("pointerout", () => setCursor("crosshair"));
          }
        });
        // Also listen for pointerover/out on the UI container itself
        this.uiLayer.on("pointerover", () => setCursor("default"));
        this.uiLayer.on("pointerout", () => setCursor("crosshair"));
      }
    }

    // Initialize match timer if duration is available
    this.initializeMatchTimer();

    // Lock chests for the first 3 minutes of the match
    this.initializeChestLocks();

    // Create reconnecting overlay (hidden by default)
    this.createReconnectingOverlay();

    // Create bad connection indicator
    this.badConnectionIndicator = new BadConnectionIndicator(this);
    this.badConnectionIndicator.create();

    // Register inventory and player info listeners
    registerInventoryAndPlayerListeners(this, this.windowManager!);
    // Register loot box listeners
    registerLootBoxListeners(this, this.windowManager!);
    // Register disconnect handler to return to loading scene on connection loss
    registerDisconnectHandler(this.game);

    // Start connection quality monitoring
    startConnectionQualityMonitoring((isBadConnection: boolean) => {
      if (isBadConnection) {
        this.badConnectionIndicator?.show();
      } else {
        this.badConnectionIndicator?.hide();
      }
    });

    // Set up shutdown event to clean up listeners when scene stops
    this.events.once("shutdown", this.shutdown, this);

    // Update inventory icon in case player data is already available
    if (this.windowManager) {
      (this.windowManager as WindowManager).updateInventoryIcon();
    }

    // Set up keyboard event listeners
    this.input.keyboard?.on("keydown", (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();

      // Handle escape key to cancel throwable mode
      if (key === "escape" && this.activeThrowableItem) {
        this.activeThrowableItem = null;
        setThrowableMode(false);

        // Stop blinking on action slots
        if (this.windowManager?.actionSlots) {
          this.windowManager.actionSlots.forEach((slot) => slot.stopBlinking());
        }
      }

      // Toggle enemy hitbox debug visualization with 'H' key
      if (key === "h") {
        this.debugHitboxes = !this.debugHitboxes;
        if (this.debugHitboxes) {
          console.info("Enemy hitboxes: ON");
          this.enemyManager.renderHitboxes();
        } else {
          console.info("Enemy hitboxes: OFF");
          this.enemyManager.clearHitboxes();
        }
      }

      this.handleAdminKeySequence(key);
    });

    // Listen for resize events to maintain camera zoom
    this.scale.on("resize", this.handleResize, this);

    // Start position update broadcast (send position to server every 100ms)
    this.startPositionBroadcast();

    this.initListeners();
  }

  private handleResize = (gameSize: Phaser.Structs.Size) => {
    // Update base zoom for mobile vs tablet vs desktop
    if (this.cameras && this.cameras.main) {
      let baseZoomValue = 1.1; // desktop default
      if (isMobile()) {
        baseZoomValue = 1.5; // phones
      } else if (isTablet()) {
        baseZoomValue = 2.2; // tablets
      }
      this.baseZoom = baseZoomValue;
      // Recalculate target zoom based on current weapon
      this.updateCameraZoomForWeapon();
    }

    // Update UI camera size to match new dimensions
    if (this.uiCamera) {
      this.uiCamera.setSize(this.scale.width, this.scale.height);
    }
  };

  /**
   * Updates the camera zoom based on equipped weapon's zoom property.
   * Uses the weapon's zoom stat, defaulting to 1.0 when unarmed or weapon has no ammo.
   */
  public updateCameraZoomForWeapon() {
    if (!this.player || !this.player.data || !this.player.data.equipment) {
      this.targetZoom = this.baseZoom;
      return;
    }

    const equippedWeapon: Item | undefined = this.player.data.equipment.Weapon;

    // Default zoom multiplier (unarmed)
    let zoomMultiplier = 1.0;

    // Check if weapon exists and has ammo
    if (equippedWeapon && equippedWeapon.definition.stats) {
      const hasAmmo =
        equippedWeapon.currentAmmo === undefined ||
        equippedWeapon.currentAmmo > 0;

      if (hasAmmo && equippedWeapon.definition.stats.zoom !== undefined) {
        zoomMultiplier = equippedWeapon.definition.stats.zoom;
      }
    }

    this.targetZoom = this.baseZoom * zoomMultiplier;
  }

  private startPositionBroadcast() {
    // Send position to server every 100ms
    this.positionUpdateInterval = this.time.addEvent({
      delay: 100,
      callback: () => {
        if (this.player && this.player.isAlive()) {
          // Only send if player moved significantly (at least 1 pixel)
          const dx = Math.abs(this.player.x - this.lastSentPosition.x);
          const dy = Math.abs(this.player.y - this.lastSentPosition.y);

          if (dx > 1 || dy > 1) {
            this.socket.emit("player-position-update", {
              x: this.player.x,
              y: this.player.y,
            });
            this.lastSentPosition.x = this.player.x;
            this.lastSentPosition.y = this.player.y;
          }
        }
      },
      loop: true,
    });
  }

  private stopPositionBroadcast() {
    if (this.positionUpdateInterval) {
      this.positionUpdateInterval.destroy();
      this.positionUpdateInterval = undefined;
    }
  }

  private handleAdminKeySequence(key: string) {
    // Reset timeout
    if (this.sequenceResetTimeout) {
      clearTimeout(this.sequenceResetTimeout);
    }

    // Add key to current sequence
    this.currentKeySequence.push(key);

    // Check if we have a match so far
    const isCorrectSoFar = this.currentKeySequence.every(
      (k, index) => k === this.adminKeySequence[index],
    );

    if (!isCorrectSoFar) {
      // Wrong key, reset sequence
      this.currentKeySequence = [];
      return;
    }

    // Check if sequence is complete
    if (this.currentKeySequence.length === this.adminKeySequence.length) {
      // Admin sequence completed! Show the admin button
      this.currentKeySequence = [];
      if (this.windowManager) {
        this.windowManager.showAdminButton();
      }
      return;
    }

    // Set timeout to reset sequence after 2 seconds of inactivity
    this.sequenceResetTimeout = setTimeout(() => {
      this.currentKeySequence = [];
    }, 2000);
  }

  initListeners() {
    // Set up throwable event listeners
    this.events.on("throwable-activated", (item: Item) => {
      this.activeThrowableItem = item;
      if (isTouchDevice()) {
        setThrowableMode(true);
      }
    });

    // Check if this is a reconnection
    const reconnectData = (window as any).reconnectData;
    if (reconnectData) {
      // Clear the reconnect data
      delete (window as any).reconnectData;

      // Wait for player to be created, then restore state
      this.time.delayedCall(100, () => {
        if (this.player && reconnectData.position) {
          this.player.setPositionInstant(
            reconnectData.position.x,
            reconnectData.position.y,
          );
          if (
            reconnectData.health !== undefined &&
            reconnectData.maxHealth !== undefined
          ) {
            this.player.updateHealth(
              reconnectData.health,
              reconnectData.maxHealth,
              reconnectData.armor,
              reconnectData.maxArmor,
            );
          }
        }
      });
    }

    this.socket.emit("player-enter-main");

    // Show first-match tutorial if not yet seen
    this.socket.on("tutorials-data", (tutorials: string[]) => {
      tutorialHandler.initializeWithAccountData(tutorials);
      tutorialHandler.loadTutorialsFromFile().then(() => {
        if (tutorialHandler.shouldShow("first-match") && this.windowManager) {
          const tutorial = tutorialHandler.getTutorial("first-match");
          if (tutorial) {
            this.windowManager.showTutorial("first-match", tutorial, () => {
              tutorialHandler.markAsSeen("first-match");
              if (window.socket) {
                window.socket.emit("update-tutorials", {
                  tutorials: tutorialHandler.getSeenTutorials(),
                });
              }
            });
          }
        }
      });
    });

    // Listen for squad code from server (sent immediately after state event)
    this.socket.on(
      "state-with-squad-code",
      (data: { squadCode: string | null }) => {
        this.mySquadCode = data.squadCode || undefined;
        console.info(
          `[CLIENT] Received squad code: ${this.mySquadCode || "none"}`,
        );

        // Now that we have our squad code, show squad indicators for squad members
        if (this.mySquadCode) {
          Object.entries(this.otherPlayers).forEach(([playerId, player]: [string, Player]) => {
            // Find the player data from the state to check their squad code
            // Since we already received the state, we can check if they should have the indicator
            // The player state has squadCode, so we need to check it
            // But we need to store it when we create the player or update it here
            // For now, just show indicator for any player that matches our squad code
            // We'll need to track squad code in the player object
            if (
              player &&
              (player as any).squadCode &&
              (player as any).squadCode === this.mySquadCode
            ) {
              player.showSquadIndicator();
            }
          });
        }
      },
    );

    this.events.on(
      "throwable-release",
      (throwData: {
        itemDefId: number;
        startX: number;
        startY: number;
        direction: { x: number; y: number };
        distance: number;
      }) => {
        if (this.activeThrowableItem && this.player) {
          // Guard: Only allow one throw per item selection
          const itemToThrow = this.activeThrowableItem;
          this.activeThrowableItem = null;

          // Consume the item from inventory
          window.socket.emit("use-item", {
            itemId: itemToThrow.id,
            slotType: SlotType.ACTION,
          });

          setThrowableMode(false);

          // Stop blinking on action slots
          if (this.windowManager?.actionSlots) {
            this.windowManager.actionSlots.forEach((slot) =>
              slot.stopBlinking(),
            );
          }

          window.socket.emit("throwable-thrown", {
            itemDefId: throwData.itemDefId,
            startX: throwData.startX,
            startY: throwData.startY,
            distance: throwData.distance,
            direction: throwData.direction,
          });
        }
      },
    );

    this.socket.on(
      "state",
      (players: {
        [id: string]: {
          id: string;
          x: number;
          y: number;
          weapon?: any;
          activeSkin?: string;
          equippedHat?: number;
          squadCode?: string;
          accountName?: string;
        };
      }) => {
        // Mark as just reconnected so next position update is instant
        this.initialPlayers = players;
        this.initialStateReceived = true;
        // If scene is already created, ensure all players are present
        if (this.scene.isActive()) {
          Object.values(players).forEach((p: {
              id: string;
              x: number;
              y: number;
              weapon?: any;
              activeSkin?: string;
              equippedHat?: number;
              squadCode?: string;
              accountName?: string;
            }) => {
              if (p.id === this.socket.id) {
                // Remove old sprite if reconnecting (player had a disconnected sprite in otherPlayers)
                if (this.otherPlayers[p.id]) {
                  this.otherPlayers[p.id].destroy();
                  delete this.otherPlayers[p.id];
                }

                if (!this.player) {
                  // Use reconnection data if available, otherwise use server position
                  let spawnX = p.x;
                  let spawnY = p.y;
                  let health = 100;
                  let maxHealth = 100;

                  if (this.isReconnecting && this.reconnectionData) {
                    spawnX = this.reconnectionData.position.x;
                    spawnY = this.reconnectionData.position.y;
                    health = this.reconnectionData.health;
                    maxHealth = this.reconnectionData.maxHealth;
                  }

                  this.player = new Player(
                    p.id,
                    this,
                    spawnX,
                    spawnY,
                    health,
                    maxHealth,
                  );

                  if (this.isReconnecting && this.reconnectionData) {
                    this.player.updateHealth(
                      health,
                      maxHealth,
                      this.reconnectionData.armor,
                      this.reconnectionData.maxArmor,
                    );
                  }
                  // Apply active skin - use server data if provided, fallback to previous player data if not
                  if ("activeSkin" in p) {
                    // Server explicitly sent activeSkin (could be undefined for default skin)
                    this.player.data.activeSkin = p.activeSkin;
                    this.player.updateSkin(p.activeSkin);
                  } else if (window.player?.data?.activeSkin) {
                    // Fallback only if server didn't provide activeSkin
                    this.player.data.activeSkin = window.player.data.activeSkin;
                    this.player.updateSkin(window.player.data.activeSkin);
                  }

                  // Apply weapon - use server data if provided, fallback to previous player data if not
                  const inheritedWeapon3 = p.weapon ?? window.player?.data?.equipment?.Weapon;
                  if (inheritedWeapon3) {
                    this.player.data.equipment.Weapon = inheritedWeapon3;
                    this.player.updateWeaponSprite();
                  }

                  // Apply equipped hat - use server data if provided, fallback to previous player data if not
                  this.player.data.equippedHat = "equippedHat" in p ? p.equippedHat : window.player?.data?.equippedHat;
                  this.player.updateHatSprite();

                  // Set up callbacks to close windows on movement and damage
                  this.player.setOnMoveCallback(() => {
                    if (this.windowManager) {
                      this.windowManager.closeAllWindows();
                    }
                  });
                  this.player.setOnDamageCallback(() => {
                    if (this.damageFlash) {
                      this.damageFlash.flash();
                    }
                  });
                  window.player = this.player;
                  // Ensure desktop controls are set up after player creation
                  if (!isTouchDevice()) {
                    setupDesktopControls(this);
                  }
                  // Update camera zoom based on initial equipment
                  this.updateCameraZoomForWeapon();
                  // Show beam up effect when player first appears (but not on reconnection)
                  if (!this.isReconnecting) {
                    showBeamUpParticle(
                      this,
                      this.player.container.x,
                      this.player.container.y,
                    );
                  }

                  // Reset reconnection flag after use
                  this.isReconnecting = false;
                  this.reconnectionData = undefined;
                } else {
                  // Player already exists (fallback player), update position and make visible
                  this.player.setPositionInstant(p.x, p.y);
                  this.player.container.setVisible(true);
                  // Update skin from server data - prioritize server data if provided
                  if ("activeSkin" in p) {
                    this.player.data.activeSkin = p.activeSkin;
                    this.player.updateSkin(p.activeSkin);
                  }
                  // Apply weapon from state data
                  const inheritedWeapon2 = p.weapon ?? window.player?.data?.equipment?.Weapon;
                  if (inheritedWeapon2) {
                    this.player.data.equipment.Weapon = inheritedWeapon2;
                    this.player.updateWeaponSprite();
                  } else {
                    this.player.updateWeaponSprite();
                  }
                  // Update hat from state data
                  if ("equippedHat" in p) {
                    this.player.data.equippedHat = p.equippedHat;
                    this.player.updateHatSprite();
                  }
                  // Controls were already set up in create() and use scene.player dynamically,
                  // so no need to re-register them here (would cause double movement).
                  // Update camera zoom based on equipment
                  this.updateCameraZoomForWeapon();
                  // Show beam up effect when player becomes visible
                  showBeamUpParticle(
                    this,
                    this.player.container.x,
                    this.player.container.y,
                  );
                }
              } else {
                // Remove old sprite if player is reconnecting
                if (this.otherPlayers[p.id]) {
                  this.otherPlayers[p.id].destroy();
                  delete this.otherPlayers[p.id];
                }

                // Create new sprite for other player
                this.otherPlayers[p.id] = new Player(
                  p.id,
                  this,
                  p.x,
                  p.y,
                  100,
                  100,
                  true,
                );
                // Store squad code and accountId on player for later reference
                (this.otherPlayers[p.id] as any).squadCode =
                  p.squadCode || null;
                (this.otherPlayers[p.id] as any).accountId =
                  (p as any).accountId || null;

                // Apply account name if provided
                if (p.accountName) {
                  this.otherPlayers[p.id].data.name = p.accountName;
                  this.otherPlayers[p.id].healthbar?.setName(p.accountName);
                }

                // Set weapon equipment if provided
                if (p.weapon) {
                  if (!this.otherPlayers[p.id].data.equipment) {
                    this.otherPlayers[p.id].data.equipment = {};
                  }
                  this.otherPlayers[p.id].data.equipment.Weapon = p.weapon;
                  this.otherPlayers[p.id].updateWeaponSprite();
                }
                // Apply active skin if provided
                if (p.activeSkin) {
                  this.otherPlayers[p.id].data.activeSkin = p.activeSkin;
                  this.otherPlayers[p.id].updateSkin(p.activeSkin);
                }
                // Apply equipped hat if provided
                this.otherPlayers[p.id].data.equippedHat = p.equippedHat;
                this.otherPlayers[p.id].updateHatSprite();
                // Show squad indicator if this player is in same squad
                if (p.squadCode && p.squadCode === this.mySquadCode) {
                  this.otherPlayers[p.id].showSquadIndicator();
                }
                // Make sure UI camera ignores other players
                if (this.uiCamera && this.otherPlayers[p.id].container) {
                  this.uiCamera.ignore(this.otherPlayers[p.id].container);
                }
              }
            },
          );
        }
      },
    );
    this.socket.on(
      "teleports-active",
      (teleports: Array<{ name: string; x: number; y: number }>) => {
        this.mapHandler!.teleportsActive = teleports;
        // If map is already created, render teleports now
        this.mapHandler!.renderTeleportSprites();
      },
    );

    // Listen for active teleport reveal (after 2 minutes)
    this.socket.on(
      "active-teleport",
      async (data: { x: number; y: number; name: string }) => {
        // Store the active teleport position for the arrow to point towards
        (this as any).activeTeleportPosition = { x: data.x, y: data.y };

        // Play teleport sound effect
        playSound(this, "teleport");

        // Render the animated teleport sprite at the active teleport location
        if (this.mapHandler) {
          this.mapHandler.renderActiveTeleportSprite(data.x, data.y);
        }

        // Show the teleport arrow if it hasn't been created yet
        if (this.uiLayer && !(this as any).teleportArrow) {
          const { TeleportArrow } = await import("../ui/teleport-arrow");
          const arrow = new TeleportArrow(this, this.uiLayer, data.x, data.y);
          (this as any).teleportArrow = arrow;
        } else if ((this as any).teleportArrow) {
          // Update existing arrow target
          ((this as any).teleportArrow as any).setTargetTile(data.x, data.y);
        }
      },
    );

    // Listen for other-player-died event (for all clients)
    this.socket.on("other-player-died", (playerId: string) => {
      const otherPlayer = this.otherPlayers[playerId];
      if (otherPlayer) {
        if (!playerId) {
          console.warn(
            "other-player-died event received with null/undefined playerId",
          );
          return;
        }
        const otherPlayer = this.otherPlayers[playerId];
        if (otherPlayer) {
          otherPlayer.destroy();
          delete this.otherPlayers[playerId];
        } else {
          console.warn(
            "other-player-died: No otherPlayer found for playerId",
            playerId,
          );
        }
      }
    });

    // Show the killer's profile pic + banner text (broadcast to the whole instance
    // with a victimId, so we filter to only react when it's us).
    this.socket.on("player-killed-info", (data: KillerInfo) => {
      if (data.victimId !== this.socket.id) return;
      if (!this.uiLayer) return;
      (this as any).killMessage?.destroy();
      (this as any).killMessage = new KillMessage(this, this.uiLayer, data);
    });

    // Sprays placed by any player — broadcast always includes the sender (see
    // emitToInstanceBySocket), so there's no local optimistic placement to reconcile.
    this.socket.on(
      "sticker-placed",
      (data: { stickerId: string; spriteKey?: string; sprayId: number; x: number; y: number }) => {
        const textureKey = data.spriteKey || `spray-${data.sprayId}`;
        this.stickerManager?.place(data.stickerId, textureKey, data.x, data.y);
      },
    );

    // Sent once on join/reconnect: replays every sticker already placed in this
    // match so a late-joining or reconnecting player's view matches everyone else's.
    this.socket.on(
      "stickers-list",
      (stickers: { stickerId: string; spriteKey?: string; sprayId: number; x: number; y: number }[]) => {
        if (!Array.isArray(stickers)) return;
        stickers.forEach((s) => {
          const textureKey = s.spriteKey || `spray-${s.sprayId}`;
          this.stickerManager?.place(s.stickerId, textureKey, s.x, s.y);
        });
      },
    );

    this.socket.on("match-sticker-cleanup", () => {
      this.stickerManager?.removeAll();
    });

    // Register player syncing listeners (handles playerJoined, playerState, playerLeft)
    registerPlayerListeners(this, {
      otherPlayers: this.otherPlayers,
      mySquadCode: this.mySquadCode,
      uiCamera: this.uiCamera,
      adapterType: "instance",
      windowManager: this.windowManager,
    });

    // Handle own player position updates (server reconciliation)
    this.socket.on(
      "playerState",
      (data: {
        id: string;
        x: number;
        y: number;
        lastProcessedInput?: number;
      }) => {
        if (data.id === this.socket.id && this.player) {
          this.player.socketId = data.id;
          // This is a position correction from server
          // Make sure player is visible when receiving position updates
          if (!this.player.container.visible) {
            this.player.container.setVisible(true);
          }
          if (this.firstConnect) {
            // Instantly set player position after reconnect
            this.player.setPositionInstant(data.x, data.y);
            this.lastSentPosition.x = data.x;
            this.lastSentPosition.y = data.y;
            // Recenter camera on player after reconnection
            // This ensures the camera immediately focuses on the correct position
            if (this.cameras.main && this.player.container) {
              this.cameras.main.stopFollow();
              this.cameras.main.startFollow(
                this.player.container,
                true,
                0.08,
                0.08,
              );
            }
            this.firstConnect = false;
          } else {
            // Server sent a correction (e.g., player was on invalid tile)
            // Always apply corrections instantly to prevent client-side interpolation lag
            this.player.setPositionInstant(data.x, data.y);
            this.lastSentPosition.x = data.x;
            this.lastSentPosition.y = data.y;
          }
        }
      },
    );

    // Listen for target-hit messages from server
    this.socket.on(
      "target-hit",
      (data: {
        targetType: string;
        targetId: string;
        damage: number;
        health: number;
        maxHealth: number;
        armor?: number;
        maxArmor?: number;
        armorItems?: Array<{ current: number; max: number; color?: string }>;
        attackerId?: string;
        hitPos?: { x: number; y: number };
      }) => {
        let target = null;
        if (data.targetType === "enemy") {
          if (this.enemyManager && this.enemyManager.enemies[data.targetId]) {
            const enemy = this.enemyManager.enemies[data.targetId];
            const prevHealth = enemy.health;
            enemy.health = data.health;
            enemy.maxHealth = data.maxHealth;
            target = enemy;

            // Show health bar temporarily if hit by user player
            if (data.attackerId === this.socket.id && enemy.healthbar) {
              enemy.healthbar.showTemporarily(4000);
            }

            // Show animated damage effect at actual hit position if available
            let px, py;
            if (
              data.hitPos &&
              typeof data.hitPos.x === "number" &&
              typeof data.hitPos.y === "number"
            ) {
              px = data.hitPos.x;
              py = data.hitPos.y;
            } else {
              px = enemy.container ? enemy.container.x : enemy.sprite.x;
              py = enemy.container ? enemy.container.y : enemy.sprite.y;
            }
            showSplashParticle(this, px, py);

            // Optionally show damage text or animation here
          }
        } else if (data.targetType === "player") {
          if (this.player && this.player.socketId === data.targetId) {
            const prevHealth = this.player.health;
            this.player.updateHealth(data.health, data.maxHealth, data.armor, data.maxArmor, data.armorItems);
            target = this.player;

            // Trigger damage flash
            if (this.damageFlash) {
              this.damageFlash.flash();
            }

            // Show splash particle
            showSplashParticle(this, this.player.x, this.player.y);
          } else if (this.otherPlayers[data.targetId]) {
            const prevHealth = this.otherPlayers[data.targetId].health;
            this.otherPlayers[data.targetId].updateHealth(
              data.health,
              data.maxHealth,
              data.armor,
              data.maxArmor,
              data.armorItems,
            );
            target = this.otherPlayers[data.targetId];

            // Show health bar temporarily if hit by user player
            if (data.attackerId === this.socket.id && target.healthbar) {
              target.healthbar.showTemporarily(4000);
            }

            // Show splash particle
            showSplashParticle(this, target.x, target.y);
          }
        }
        if (target) {
          spawnBloodSplatter(this, target.x, target.y, this.bloodSplatters);
        }
      },
    );

    // Listen for attack events and animate attack lines
    this.socket.on("attack", (data: AttackEventData) => {
      // Check if attackAnimationManager is still available
      if (!this.attackAnimationManager) {
        return;
      }

      // Check if this is an unarmed/melee attack
      const isUnarmedAttack =
        data.range !== undefined && data.range <= PLAYER_UNARMED_ATTACK_RANGE;

      // Use client-side bot/enemy sprite position for animation start if attacker is enemy/bot
      let attackerX = data.attackerX;
      let attackerY = data.attackerY;
      if (
        data.attackerId.startsWith("enemy_") ||
        data.attackerId.startsWith("bot_") ||
        data.attackerId.startsWith("boss_")
      ) {
        const enemyObj = this.enemyManager.enemies[data.attackerId];
        // Use container position if available, else fallback to sprite position
        if (enemyObj) {
          const container = (enemyObj as any).container;
          if (
            container &&
            typeof container.x === "number" &&
            typeof container.y === "number"
          ) {
            attackerX = container.x;
            attackerY = container.y;
          } else {
            attackerX = enemyObj.sprite.x;
            attackerY = enemyObj.sprite.y;
          }
        }
      }

      // Play weapon attack sound
      let weaponSfx: string | undefined;

      // Check if it's the local player attacking
      if (data.attackerId === this.socket.id && this.player) {
        const equippedWeapon: Item | undefined =
          this.player.data.equipment?.Weapon;
        if (equippedWeapon && equippedWeapon.definition.sfx) {
          weaponSfx = equippedWeapon.definition.sfx;
        }
      }
      // Check if it's another player attacking
      else if (this.otherPlayers[data.attackerId]) {
        const otherPlayer = this.otherPlayers[data.attackerId];
        const equippedWeapon: Item | undefined =
          otherPlayer.data?.equipment?.Weapon;
        if (equippedWeapon && equippedWeapon.definition.sfx) {
          weaponSfx = equippedWeapon.definition.sfx;
        }
      }
      // Check if it's an enemy/bot/boss attacking
      else if (
        data.attackerId.startsWith("enemy_") ||
        data.attackerId.startsWith("bot_") ||
        data.attackerId.startsWith("boss_")
      ) {
        const enemyObj = this.enemyManager.enemies[data.attackerId];
        if (enemyObj && enemyObj.weapon && enemyObj.weapon.definition.sfx) {
          weaponSfx = enemyObj.weapon.definition.sfx;
        }
      }

      const soundGeo = {
        tileX: Math.floor(attackerX / TILE_SIZE),
        tileY: Math.floor(attackerY / TILE_SIZE),
      };

      // Play the weapon sound if available, or unarmed attack sound for melee
      if (isUnarmedAttack) {
        // Play unarmed attack sound for melee attacks
        playSound(this, "unarmed-attack", soundGeo);
      } else if (weaponSfx) {
        playSound(this, weaponSfx, soundGeo);
      }

      // Play hit sound when attack hits a target
      if (data.hit) {
        playSound(this, "hit", soundGeo);
      }

      // Rotate other players' weapons when they attack
      const otherPlayer = this.otherPlayers[data.attackerId];
      if (
        otherPlayer &&
        otherPlayer.weaponSprite &&
        otherPlayer.weaponSprite.visible
      ) {
        const dx = data.targetX - attackerX;
        const dy = data.targetY - attackerY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0.01) {
          const direction = { x: dx / dist, y: dy / dist };
          otherPlayer.rotateWeapon(direction);
          otherPlayer.lastAttackDirection = direction;
        }
      }

      // Rotate enemy/bot weapons when they attack
      if (
        data.attackerId.startsWith("enemy_") ||
        data.attackerId.startsWith("bot_") ||
        data.attackerId.startsWith("boss_")
      ) {
        const enemyObj = this.enemyManager.enemies[data.attackerId];
        if (
          enemyObj &&
          enemyObj.weaponSprite &&
          enemyObj.weaponSprite.visible
        ) {
          const dx = data.targetX - attackerX;
          const dy = data.targetY - attackerY;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 0.01) {
            const direction = { x: dx / dist, y: dy / dist };
            Enemy.rotateWeapon(
              enemyObj.weaponSprite,
              direction,
              enemyObj.scale || 1,
            );
          }
        }
      } // Only show animated line for ranged attacks (not unarmed)
      if (!isUnarmedAttack) {
        let color = data.attackerId.startsWith("enemy_")
          ? data.hit
            ? 0xff4444
            : 0x880000
          : data.hit
            ? 0xffd700
            : 0xffffff;
        this.attackAnimationManager.addAttackAnimation({
          attackerX,
          attackerY,
          targetX: data.targetX,
          targetY: data.targetY,
          color,
          duration: 250,
        });
      }

      // Show melee attack sprite for unarmed attacks (both player and enemy)
      if (isUnarmedAttack) {
        this.meleeAttackSpriteManager.showMeleeAttack(
          attackerX,
          attackerY,
          data.targetX,
          data.targetY,
        );
      }

      // Show gray splash particle effect if attack was blocked by obstacle
      if (data.blocked) {
        showGraySplashParticle(this, data.targetX, data.targetY);
      }
    });

    // Register item/loot-bag syncing listeners (handles loot-bag-update, add-loot-bag, update-loot-bag-sprite)
    registerItemListeners(this, this.windowManager);

    // Listen for air drop event from server
    this.socket.on(
      "air-drop-event",
      (data: {
        position: { x: number; y: number };
        chests: Array<{ x: number; y: number }>;
      }) => {
        // Show air drop notification
        if (this.player) {
          const notification = this.add
            .text(this.cameras.main.centerX, 100, "AIR DROP INCOMING!", {
              fontSize: "48px",
              color: COLORS.WHITE,
              fontFamily: "Anton",
              shadow: {
                offsetX: 0,
                offsetY: 6,
                color: "#000000",
                blur: 0,
                stroke: true,
                fill: true,
              },
            })
            .setOrigin(0.5, 0.5)
            .setScrollFactor(0)
            .setDepth(10000);

          // Make notification only visible to UI camera, not main camera
          if (this.cameras.main) {
            this.cameras.main.ignore(notification);
          }

          // Fade out and remove after 3 seconds
          this.tweens.add({
            targets: notification,
            alpha: 0,
            duration: 3000,
            delay: 2000,
            onComplete: () => {
              notification.destroy();
            },
          });
        }

        // Play airdrop sound
        playSound(this, "airdrop");

        // Only show airplane animation if player is not indoors
        const isPlayerIndoors = this.player && this.mapHandler
          ? this.mapHandler.isIndoorTile(this.player.x, this.player.y)
          : false;
        if (!isPlayerIndoors) {
          // Animate airplane sprite
          this.animateAirplane(data.position);
        }

        // Show parachute at the air drop center position
        const centerWorldX = data.position.x * TILE_SIZE + TILE_SIZE / 2;
        const centerWorldY = data.position.y * TILE_SIZE + TILE_SIZE / 2;
        const parachute = this.add.image(
          centerWorldX,
          centerWorldY,
          "parachute",
        );
        // Use depth 3 (tile-effect depth, same as tile layers)
        parachute.setDepth(3);
        parachute.setScale(1); // Half size
        parachute.setScrollFactor(1); // Move with camera (world object)

        // Make parachute only visible to main camera, not UI camera
        if (this.uiCamera) {
          this.uiCamera.ignore(parachute);
        }
      },
    );

    // Listen for air drop parachute position (sent when joining/reconnecting to show existing air drop)
    this.socket.on(
      "air-drop-parachute",
      (data: { position: { x: number; y: number } }) => {
        // Create parachute at the air drop position
        const centerWorldX = data.position.x * TILE_SIZE + TILE_SIZE / 2;
        const centerWorldY = data.position.y * TILE_SIZE + TILE_SIZE / 2;
        const parachute = this.add.image(
          centerWorldX,
          centerWorldY,
          "parachute",
        );
        parachute.setDepth(3); // tile-effect depth
        parachute.setScale(1); // Half size
        parachute.setScrollFactor(1);

        // Make parachute only visible to main camera, not UI camera
        if (this.uiCamera) {
          this.uiCamera.ignore(parachute);
        }
      },
    );

    // Listen for air strike warning event
    this.socket.on("air-strike-warning", (data: { playerCount: number }) => {
      // Play looping alarm sound
      // Accept warningStartTime for countdown sync
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const warningData = data as {
        playerCount: number;
        warningStartTime?: number;
      };
      this.sound.play("alarm", { loop: true });

      // Check if player is indoors
      const isPlayerIndoors = this.player && this.mapHandler
        ? this.mapHandler.isIndoorTile(this.player.x, this.player.y)
        : false;

      // Show crosshair at center of screen (will be hidden if player is indoors)
      const crosshair = this.add.image(
        this.cameras.main.centerX,
        this.cameras.main.centerY,
        "airstrike-crosshair",
      );
      crosshair.setScrollFactor(0); // Stay in UI space
      crosshair.setDepth(10001); // Just below warning text
      crosshair.setAlpha(isPlayerIndoors ? 0 : 0.7); // Hidden if indoors, visible if outdoors

      // Make crosshair only visible to UI camera, not main camera
      if (this.cameras.main) {
        this.cameras.main.ignore(crosshair);
      }

      // Update crosshair visibility as player moves
      const crosshairUpdateInterval = setInterval(() => {
        if (!this.player) return;

        const tileX = Math.floor(this.player.x / TILE_SIZE);
        const tileY = Math.floor(this.player.y / TILE_SIZE);
        const tilesLayer = this.mapHandler?.getLayer("tiles");
        let currentlyIndoors = false;

        if (tilesLayer) {
          const tile = tilesLayer.getTileAt(tileX, tileY);
          currentlyIndoors = this.player && this.mapHandler
            ? this.mapHandler.isIndoorTile(this.player.x, this.player.y)
            : false;
        }

        // Update crosshair alpha based on indoor status
        crosshair.setAlpha(currentlyIndoors ? 0 : 0.7);
      }, 100); // Check every 100ms

      // Show warning text at the top of the screen
      const warningText = this.add
        .text(this.cameras.main.centerX, 80, i18next.t("scenes:main.airStrikeWarning"), {
          fontSize: "32px",
          color: "#FF0000",
          fontFamily: "Anton",
          shadow: {
            offsetX: 0,
            offsetY: 6,
            color: "#000000",
            blur: 0,
            stroke: true,
            fill: true,
          },
        })
        .setOrigin(0.5, 0.5)
        .setScrollFactor(0)
        .setDepth(10000);

      // Make text only visible to UI camera, not main camera
      if (this.cameras.main) {
        this.cameras.main.ignore(warningText);
      }

      // Add blinking animation to warning text
      this.tweens.add({
        targets: warningText,
        alpha: [1, 0.3, 1],
        duration: 600,
        repeat: -1, // Loop continuously
      });

      // Show "take cover" message below the warning text
      const takeCoverText = this.add
        .text(this.cameras.main.centerX, 115, i18next.t("scenes:main.takeCoverInside"), {
          fontSize: "20px",
          color: "#FF0000",
          fontFamily: "Anton",
          shadow: {
            offsetX: 0,
            offsetY: 4,
            color: "#000000",
            blur: 0,
            stroke: true,
            fill: true,
          },
        })
        .setOrigin(0.5, 0.5)
        .setScrollFactor(0)
        .setDepth(10000);

      // Make text only visible to UI camera, not main camera
      if (this.cameras.main) {
        this.cameras.main.ignore(takeCoverText);
      }

      // Add blinking animation to take cover text
      this.tweens.add({
        targets: takeCoverText,
        alpha: [1, 0.3, 1],
        duration: 600,
        repeat: -1, // Loop continuously
      });

      // Calculate synced countdown
      const now = Date.now();
      const warningStartTime = warningData.warningStartTime || now;
      let secondsRemaining = Math.max(
        0,
        AIR_STRIKE_WARNING_DURATION_MS / 1000 -
          Math.floor((now - warningStartTime) / 1000),
      );

      // Create timer text below warning
      const timerText = this.add
        .text(this.cameras.main.centerX, 165, secondsRemaining.toString(), {
          fontSize: "40px",
          color: "#FF0000",
          fontFamily: "Anton",
          shadow: {
            offsetX: 0,
            offsetY: 4,
            color: "#000000",
            blur: 0,
            stroke: true,
            fill: true,
          },
        })
        .setOrigin(0.5, 0.5)
        .setScrollFactor(0)
        .setDepth(10000);

      // Make timer only visible to UI camera
      if (this.cameras.main) {
        this.cameras.main.ignore(timerText);
      }

      // Store references for removal on explosion
      this._airStrikeWarningUI = {
        timerText,
        warningText,
        takeCoverText,
        crosshair,
        crosshairUpdateInterval,
        timerInterval: null as any, // will be set below
      };

      // Update timer countdown
      const timerInterval = setInterval(() => {
        secondsRemaining--;
        timerText.setText(secondsRemaining.toString());

        if (secondsRemaining <= 0) {
          clearInterval(timerInterval);
          timerText.destroy();
        }
      }, 1000);
      this._airStrikeWarningUI.timerInterval = timerInterval;

      // Fade out and remove warning text after the correct remaining time
      this.time.delayedCall(secondsRemaining * 1000, () => {
        // Clean up all airstrike warning UI elements and intervals
        if (this._airStrikeWarningUI) {
          const {
            timerText,
            warningText,
            takeCoverText,
            crosshair,
            crosshairUpdateInterval,
            timerInterval,
          } = this._airStrikeWarningUI;
          if (timerText && timerText.destroy) timerText.destroy();
          if (warningText && warningText.destroy) {
            this.tweens.add({
              targets: warningText,
              alpha: 0,
              duration: 500,
              onComplete: () => {
                warningText.destroy();
              },
            });
          }
          if (takeCoverText && takeCoverText.destroy) {
            this.tweens.add({
              targets: takeCoverText,
              alpha: 0,
              duration: 500,
              onComplete: () => {
                takeCoverText.destroy();
              },
            });
          }
          if (crosshair && crosshair.destroy) crosshair.destroy();
          if (crosshairUpdateInterval) clearInterval(crosshairUpdateInterval);
          if (timerInterval) clearInterval(timerInterval);
          this._airStrikeWarningUI = undefined;
        }
      });
    });

    // Listen for air strike explosion event
    this.socket.on(
      "air-strike-airplane",
      (data: { position: { x: number; y: number } }) => {
        // Play airdrop sound
        playSound(this, "airdrop");

        // Only show airplane animation if player is not indoors
        const isPlayerIndoors = this.player && this.mapHandler
          ? this.mapHandler.isIndoorTile(this.player.x, this.player.y)
          : false;
        if (!isPlayerIndoors) {
          // Animate airplane sprite
          this.animateAirplane(data.position);
        }
      },
    );

    this.socket.on(
      "air-strike-explosion",
      (data: {
        positions: Array<{
          x: number;
          y: number;
          radius: number;
          scale?: number;
        }>;
      }) => {
        // Stop the alarm sound
        this.sound.stopByKey("alarm");

        // Remove airstrike warning UI if present
        if (this._airStrikeWarningUI) {
          const {
            timerText,
            warningText,
            takeCoverText,
            crosshair,
            crosshairUpdateInterval,
            timerInterval,
          } = this._airStrikeWarningUI;
          if (timerText && timerText.destroy) timerText.destroy();
          if (warningText && warningText.destroy) warningText.destroy();
          if (takeCoverText && takeCoverText.destroy) takeCoverText.destroy();
          if (crosshair && crosshair.destroy) crosshair.destroy();
          if (crosshairUpdateInterval) clearInterval(crosshairUpdateInterval);
          if (timerInterval) clearInterval(timerInterval);
          this._airStrikeWarningUI = undefined;
        }

        // Play single explosion sound for the whole air strike
        playSound(this, "explosion");

        // Create explosion sprites at each exact position
        data.positions.forEach((explosionData) => {
          const x = explosionData.x;
          const y = explosionData.y;
          const scale = explosionData.scale || 1;
          this.createAirStrikeExplosion(x, y, scale);
        });
      },
    );

    // Force close loot window when server regenerates loot boxes
    this.socket.on("force-close-loot", () => {
      if (this.windowManager && this.windowManager.isLootWindowVisible()) {
        this.windowManager.hideLootWindow();
      }
    });

    // Listen for health-update events from server (e.g., when using medic items)
    this.socket.on(
      "health-update",
      (data: { health: number; maxHealth: number; armor?: number; maxArmor?: number; healAmount: number }) => {
        if (this.player) {
          this.player.updateHealth(data.health, data.maxHealth, data.armor, data.maxArmor);
          // Show heal amount as green text (only if actually healing)
          if (data.healAmount > 0) {
            spawnPlayerLabel(
              `+${data.healAmount} HP`,
              this.player.x,
              this.player.y - 48,
              this,
              COLORS.GREEN,
            );
          }
        }
      },
    );

    // Listen for match-end from server
    this.socket.on(
      "match-end",
      (data: { matchEndData: MatchEndData; playerData: PlayerData }) => {
        // Track match end event with duration
        if (this.matchStartTime) {
          const durationMs = Date.now() - this.matchStartTime;
          const durationSeconds = Math.round(durationMs / 1000);
          trackEvent("match-end", {
            reason: data.matchEndData.reason,
            duration: durationSeconds,
            isNoobMatch: data.matchEndData.isNoobMatch,
          });
        }

        // Add a delay when player dies so they can see what happened and read
        // the kill message (which stays on screen for 4s) before it appears
        if (data.matchEndData.reason === "DEATH") {
          // Mark player as dead
          this.playerDead = true;

          // Close all windows and hide the rest of the match HUD so only the
          // kill message shows while the death delay plays out
          if (this.windowManager) {
            this.windowManager.closeAllWindows();
            this.windowManager.hideOpenButton();
            this.windowManager.hideLootActionButtons();
            // Sticky hide: other events (item pickups, weapon changes, etc.) call
            // updateActionSlotsVisibility() and would otherwise re-show the action
            // bar/joysticks mid-way through the kill message / death delay.
            this.windowManager.setActionUIForceHidden(true);
            this.windowManager.hideUIButtons();
            this.windowManager.hideWeaponUI();
            this.windowManager.hidePlayerWidget();
          }
          // Clear any interactable/loot-box outline the player was standing
          // next to when they died, so it doesn't stay highlighted on their corpse.
          this.interactionManager.reset();
          if (this.matchTimerText) {
            this.matchTimerText.setVisible(false);
          }

          // Hide player sprite and show big blood splash
          if (this.player && this.player.container) {
            showDeathBloodSplash(
              this,
              this.player.container.x,
              this.player.container.y,
            );
            this.player.container.setVisible(false);
          }

          this.time.delayedCall(4000, () => {
            this.backToBaseScene(data.matchEndData, data.playerData);
          });
        } else {
          this.backToBaseScene(data.matchEndData, data.playerData);
        }
      },
    );

    // Listen for admin mode status from server
    this.socket.on("admin-mode-status", (data: { adminMode: boolean }) => {
      if (this.player) {
        this.player.setAdminMode(data.adminMode);
      }
      if (this.enemyManager) {
        this.enemyManager.updateAllBehaviorText(data.adminMode);
      }
      if (this.windowManager && this.scene.isActive()) {
        try {
          this.windowManager.updateAdminButton(data.adminMode);
          if (data.adminMode) {
            this.windowManager.showAdminButton();
          }
        } catch (error) {
          console.warn("Error updating admin button in main scene:", error);
        }
      }
    });

    // Listen for other players' admin mode changes
    this.socket.on(
      "player-admin-mode",
      (data: { playerId: string; adminMode: boolean }) => {
        if (this.otherPlayers[data.playerId]) {
          this.otherPlayers[data.playerId].setAdminMode(data.adminMode);
        }
      },
    );

    // Listen for throwable projectiles from other players
    this.socket.on(
      "throwable-projectile",
      (data: {
        playerId: string;
        itemDef: ItemDefinition;
        startX: number;
        startY: number;
        targetX: number;
        targetY: number;
        flightTime: number;
      }) => {
        const itemData: ItemDefinition = data.itemDef;
        if (!itemData) {
          console.warn(
            "No item definition received in throwable-projectile event",
          );
          return;
        }
        this.throwableHandler.animateThrowFromServer(data);
      },
    );

    // Listen for throwable explosion from server
    this.socket.on(
      "throwable-explosion",
      (data: {
        playerId: string;
        x: number;
        y: number;
        aoeRadius: number;
        itemId?: number;
      }) => {
        this.throwableHandler.animateExplosionFromServer(data);
      },
    );

    // Keep local player weapon sprite in sync whenever player-data is refreshed
    this.socket.on("player-data", () => {
      if (this.player) {
        this.player.updateWeaponSprite();
      }
    });

    // Listen for other players' equipment updates (weapon changes)
    this.socket.on(
      "player-equipment-update",
      (data: { playerId: string; weapon: any }) => {
        const otherPlayer = this.otherPlayers[data.playerId];
        if (otherPlayer) {
          // Update equipment in player data
          if (data.weapon) {
            if (!otherPlayer.data.equipment) {
              otherPlayer.data.equipment = {};
            }
            otherPlayer.data.equipment.Weapon = data.weapon;
          } else {
            // Weapon unequipped
            if (otherPlayer.data.equipment) {
              delete otherPlayer.data.equipment.Weapon;
            }
          }
          // Update weapon sprite
          otherPlayer.updateWeaponSprite();
        }
      },
    );

    // Listen for other players' skin changes
    this.socket.on(
      "player-skin-changed",
      (data: { playerId: string; skinId: string }) => {
        const otherPlayer: Player = this.otherPlayers[data.playerId];
        if (otherPlayer && otherPlayer.data) {
          // Update activeSkin in player data
          otherPlayer.data.activeSkin = data.skinId;
          // Update player sprite
          if (otherPlayer.updateSkin) {
            otherPlayer.updateSkin(data.skinId);
          }
        }
      },
    );

    // Listen for other players' equipped season cosmetic changes (currently only hats affect rendering)
    this.socket.on(
      "player-cosmetic-changed",
      (data: { playerId: string; slot: string; id: number | null }) => {
        if (data.slot !== "hatSkin") return;
        const otherPlayer: Player = this.otherPlayers[data.playerId];
        if (otherPlayer && otherPlayer.data) {
          otherPlayer.data.equippedHat = data.id ?? undefined;
          otherPlayer.updateHatSprite();
        }
      },
    );
  }

  update(time: number, delta: number) {
    // delta is in milliseconds, convert to seconds for interpolation
    const deltaSeconds = delta / 1000;

    // Animate reconnecting spinner
    if (
      this.reconnectingSpinner &&
      this.reconnectingOverlay &&
      this.reconnectingOverlay.visible
    ) {
      this.reconnectingSpinner.rotation += 0.05;
    }

    // Interpolate local player for smooth movement between control updates (50ms -> 60fps)
    if (this.player) {
      this.player.interpolateToTarget(deltaSeconds);
    }
    // Also interpolate other players for smooth movement from server updates
    // Use for-loop instead of Object.values().forEach() for better performance
    for (const playerId in this.otherPlayers) {
      this.otherPlayers[playerId].interpolateToTarget(deltaSeconds);
    }
    // Interpolate enemy positions for smooth movement
    if (this.enemyManager) {
      this.enemyManager.interpolateEnemies();
      // Render hitbox debug visualization if enabled (only when debug mode is active)
      if (this.debugHitboxes) {
        this.enemyManager.renderHitboxes();
      }
    }

    // Smoothly interpolate camera zoom based on weapon range
    if (Math.abs(this.currentZoom - this.targetZoom) > 0.001) {
      this.currentZoom = Phaser.Math.Linear(
        this.currentZoom,
        this.targetZoom,
        0.05,
      );
      this.cameras.main.setZoom(this.currentZoom);
    }

    // Camera follows player centered
    if (this.player) {
      this.cameras.main.setFollowOffset(0, 0);
    }

    // Animate attack lines
    this.attackAnimationManager.update();

    // Update melee attack sprites
    this.meleeAttackSpriteManager.update();

    // Update damage texts (Phaser text objects)
    updateDamageTexts(this);

    // Tick cloud drift — hide when player is indoors
    if (this.cloudEffect) {
      const isIndoors = !!(this.player && this.mapHandler &&
        this.mapHandler.isIndoorTile(this.player.x, this.player.y));
      this.cloudEffect.setVisible(!isIndoors);
      this.cloudEffect.update(delta);
    }

    // Check roof visibility based on player position
    if (this.player && this.mapHandler) {
      this.mapHandler.checkRoofVisibility(this.player.x, this.player.y);
    }

    // Remove blood splatters after 30 seconds
    const now = Date.now();
    this.bloodSplatters = this.bloodSplatters.filter((bs) => {
      if (now - bs.createdAt > 30000) {
        bs.graphics.destroy();
        return false;
      }
      return true;
    });

    // Loot box outline rendering
    if (this.windowManager) {
      if (
        !this.playerDead &&
        this.player &&
        this.mapHandler &&
        this.mapHandler.lootBoxes
      ) {
        const blockLayer = this.mapHandler.getLayer("tile-objects-block");
        if (blockLayer) {
          const tileX = Math.floor(
            this.player.x / blockLayer.tilemap.tileWidth,
          );
          const tileY = Math.floor(
            this.player.y / blockLayer.tilemap.tileHeight,
          );

          // Update highlights using the centralized manager
          this.interactionManager.updateHighlights(
            this,
            tileX,
            tileY,
            this.player,
            [], // No interactables in main scene
            this.mapHandler.lootBoxes,
          );

          const highlightedLootBox =
            this.interactionManager.getHighlightedLootBox();
          const found = highlightedLootBox !== null;
          const lootBoxChanged = this.interactionManager.hasLootBoxChanged();

          // Track player tile position changes
          const playerTileChanged =
            tileX !== this.lastPlayerTileX || tileY !== this.lastPlayerTileY;

          if (playerTileChanged) {
            this.lastPlayerTileX = tileX;
            this.lastPlayerTileY = tileY;
          }

          // Update button based on current loot box state
          let buttonLabel: string | null = null;
          let buttonCallback: (() => void) | null = null;

          if (
            found &&
            highlightedLootBox &&
            !this.windowManager.isLootWindowVisible()
          ) {
            const lootBagSpriteInfo: LootBoxSpriteInfo = getLootBoxSpriteInfo(
              highlightedLootBox.info,
            );

            const isSingleItem: boolean =
              !highlightedLootBox.info.isAirDrop &&
              highlightedLootBox.info.items.length === 1;

            buttonLabel = isSingleItem ? "PICK UP" : "OPEN";
            buttonCallback = () => {
              const currentLootBox =
                this.interactionManager.getHighlightedLootBox();
              if (!currentLootBox) return;

              const currentItems = currentLootBox.info.items || [];
              this.windowManager?.showLootActionButtons(
                this,
                currentLootBox.info,
                currentLootBox.fixed,
                () => {
                  // Callback for multi-item OPEN button
                  (window as any).socket.emit("open-loot-box", {
                    x: currentLootBox.info.x,
                    y: currentLootBox.info.y,
                  });
                },
              );
            };
          }

          // Only update button if label changed, lootbox changed, or loot window visibility changed
          const lootWindowVisible = this.windowManager.isLootWindowVisible();
          if (buttonLabel !== this.lastButtonLabel || lootBoxChanged || lootWindowVisible !== this.lastLootWindowVisible) {
            this.lastButtonLabel = buttonLabel;
            this.lastLootWindowVisible = lootWindowVisible;

            if (buttonLabel && buttonCallback) {
              this.windowManager.hideOpenButton();
              this.windowManager.hideLootActionButtons();
              buttonCallback();
            } else {
              if (this.windowManager.isOpenButtonVisible()) {
                this.windowManager.hideOpenButton();
              }
              this.windowManager.hideLootActionButtons();
            }
          }
        } else {
          this.windowManager.hideOpenButton();
        }
      } else {
        this.windowManager.hideOpenButton();
      }
    }

    // Update bleeding icon in upper left if needed
    if (
      this.windowManager &&
      typeof this.windowManager.updateBleedingIcon === "function"
    ) {
      this.windowManager.updateBleedingIcon();
    }

    // Debug: Update player tile position text (commented out)
    /*
    if (this.player && this.playerTileText) {
      const tileX = Math.floor(this.player.x / 32);
      const tileY = Math.floor(this.player.y / 32);
      this.playerTileText.setText(`Player Tile: (${tileX}, ${tileY})`);
    }
    */
  }

  private initializeMatchTimer(): void {
    // Check if match duration was set from lobby
    if (window.matchDuration) {
      this.matchTimeRemaining = window.matchDuration;

      // Create timer text at top center of screen
      this.matchTimerText = this.add
        .text(
          this.cameras.main.centerX,
          20,
          this.formatMatchTime(this.matchTimeRemaining),
          {
            fontSize: "24px",
            color: "#ffffff",
            fontFamily: "Anton",
            fontStyle: "bold",
            stroke: "#000000",
            strokeThickness: 2,
            shadow: {
              offsetX: 0,
              offsetY: 6,
              color: "#000000",
              blur: 0,
              stroke: true,
              fill: true,
            },
          },
        )
        .setOrigin(0.5, 0)
        .setScrollFactor(0)
        .setDepth(10000);

      // Make main camera ignore the match timer so it's only rendered by UI camera
      this.cameras.main.ignore(this.matchTimerText);

      // Start timer that updates every second
      this.matchTimerEvent = this.time.addEvent({
        delay: 1000, // 1 second
        callback: this.updateMatchTimer,
        callbackScope: this,
        loop: true,
      });

      // Clear the global match duration
      delete window.matchDuration;
    }
  }

  private backToBaseScene(
    matchEndData: MatchEndData,
    playerData: PlayerData,
  ): void {
    // Stop audio manager
    if (this.audioManager) {
      this.audioManager.stop();
    }

    // Hide weapon UI before transitioning to prevent it from showing in base scene
    if (this.windowManager) {
      this.windowManager.hideWeaponUI();
      this.windowManager.hideAll();
    }

    this.windowManager?.hideLootWindow();

    // Clear all damage texts and status labels before transitioning
    clearAllDamageTexts();

    // Remove MainScene and start BaseScene
    // @ts-ignore
    if (window.addBaseSceneToGame) {
      window.addBaseSceneToGame();
    }
    this.scene.stop("MainScene");
    // Pass flag to indicate player is returning from death
    this.scene.start("BaseScene", { matchEndData, playerData });
    this.destroy();
  }

  private updateMatchTimer(): void {
    if (this.matchTimeRemaining > 0) {
      this.matchTimeRemaining--;

      // Play alarm sound every second for the last 10 seconds, unless it's
      // already looping from an active airstrike warning
      if (this.matchTimeRemaining < 10 && this.matchTimeRemaining > 0) {
        const alarmSound = this.sound.get("alarm");
        const alarmAlreadyLooping =
          alarmSound &&
          (
            alarmSound as
              | Phaser.Sound.WebAudioSound
              | Phaser.Sound.HTML5AudioSound
          ).isPlaying &&
          (alarmSound as any).loop;
        if (!alarmAlreadyLooping) {
          this.sound.play("alarm", { loop: false });
        }
      }

      if (this.matchTimerText) {
        this.matchTimerText.setText(
          this.formatMatchTime(this.matchTimeRemaining),
        );

        // Change color based on remaining time
        if (this.matchTimeRemaining <= 30) {
          this.matchTimerText.setColor("#ff0000"); // Red for last 30 seconds
        } else if (this.matchTimeRemaining <= 60) {
          this.matchTimerText.setColor("#ffff00"); // Yellow for last minute
        } else {
          this.matchTimerText.setColor("#ffffff"); // White normally
        }
      }
    } else {
      // Time's up - match should end
      if (this.matchTimerEvent) {
        this.matchTimerEvent.destroy();
        this.matchTimerEvent = undefined;
      }

      if (this.matchTimerText) {
        this.matchTimerText.setText("TIME UP!");
        this.matchTimerText.setColor("#ff0000");
      }
    }
  }

  private formatMatchTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  private initializeChestLocks(): void {
    if (!this.mapHandler?.lootBoxes.some((lb) => lb.isChest)) return;

    this.updateChestLocks();
    this.chestLockEvent = this.time.addEvent({
      delay: 1000,
      callback: this.updateChestLocks,
      callbackScope: this,
      loop: true,
    });
  }

  private updateChestLocks(): void {
    if (!this.mapHandler || !this.matchStartTime) return;

    const remainingMs =
      CHEST_LOCK_DURATION_MS - (Date.now() - this.matchStartTime);
    const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    let anyLocked = false;

    for (const lootBox of this.mapHandler.lootBoxes) {
      if (!lootBox.isChest) continue;
      if (lootBox.refreshLockState(this.matchStartTime)) {
        anyLocked = true;
        lootBox.showLockTimer(this, remainingSeconds);
      } else {
        lootBox.hideLockTimer();
      }
    }

    if (!anyLocked && this.chestLockEvent) {
      this.chestLockEvent.destroy();
      this.chestLockEvent = undefined;
    }
  }

  private handleMatchEnd(): void {
    // Stop audio manager
    if (this.audioManager) {
      this.audioManager.stop();
    }

    // Hide weapon UI before transitioning to prevent it from showing in base scene
    if (this.windowManager) {
      this.windowManager.hideWeaponUI();
    }

    // Clean up timer
    if (this.matchTimerEvent) {
      this.matchTimerEvent.destroy();
      this.matchTimerEvent = undefined;
    }

    // Return to base scene
    resetDesktopControls(this);
    if (window.addBaseSceneToGame) {
      window.addBaseSceneToGame();
    }
    this.destroy();
    this.scene.stop("MainScene");
    // When match ends naturally, also give teleport immunity
    this.scene.start("BaseScene", { playerData: { fromDeath: false } });
  }

  /** Called from the emote wheel's "stickers" tab — places the sticker at the player's position. */
  enterStickerPlacementMode(sprayId: number): void {
    if (!this.player) return;
    this.placeSticker(sprayId, this.player.x, this.player.y);
  }

  private placeSticker(sprayId: number, x: number, y: number): void {
    // No optimistic local placement — the server broadcast (which always includes
    // the sender, see emitToInstanceBySocket) is the single source of the sticker's
    // id, so there's no risk of a duplicate render under a mismatched local id.
    this.socket?.emit("place-sticker", { x, y, sprayId });
  }

  shutdown() {
    // Called when scene is stopped/transitioned away from
    console.info("[MainScene] Shutting down, cleaning up listeners");

    // Clean up all active damage text labels to prevent them from persisting
    clearAllDamageTexts();

    // Stop position broadcast
    this.stopPositionBroadcast();

    // Clean up socket listeners to prevent them from being called after shutdown
    if (this.socket) {
      this.socket.off("state");
      this.socket.off("teleports-active");
      this.socket.off("other-player-died");
      this.socket.off("player-killed-info");
      this.socket.off("sticker-placed");
      this.socket.off("stickers-list");
      this.socket.off("match-sticker-cleanup");
      this.socket.off("playerJoined");
      this.socket.off("playerLeft");
      this.socket.off("playerState");
      this.socket.off("target-hit");
      this.socket.off("attack");
      this.socket.off("loot-bag-update");
      this.socket.off("add-loot-bag");
      this.socket.off("air-drop-event");
      this.socket.off("air-strike-warning");
      this.socket.off("air-strike-airplane");
      this.socket.off("air-strike-explosion");
      this.socket.off("air-strike-execute");
      this.socket.off("force-close-loot");
      this.socket.off("health-update");
      this.socket.off("match-end");
      this.socket.off("enemy_update");
      this.socket.off("throwable-projectile");
      this.socket.off("throwable-explosion");
      this.socket.off("player-equipment-update");
      this.socket.off("player-admin-mode");
      this.socket.off("player-skin-changed");
      this.socket.off("active-teleport");
    }

    // Unregister inventory/player listeners
    unregisterInventoryAndPlayerListeners();
    unregisterLootBoxListeners();
    unregisterPlayerListeners("instance");
    unregisterItemListeners();
    // Unregister disconnect handler
    unregisterDisconnectHandler();

    // Stop connection quality monitoring
    stopConnectionQualityMonitoring();

    // Destroy bad connection indicator
    this.badConnectionIndicator?.destroy();

    // Destroy cloud effect
    if (this.cloudEffect) {
      this.cloudEffect.destroy();
      this.cloudEffect = undefined;
    }

    // Destroy firefly emitter
    if (this.fireflyEmitter) {
      this.fireflyEmitter.destroy();
      this.fireflyEmitter = undefined;
    }

    // Destroy active teleport sprite if it exists
    if ((this as any).activeTeleportSprite) {
      ((this as any).activeTeleportSprite as any).destroy();
      (this as any).activeTeleportSprite = undefined;
    }

    // Destroy teleport arrow if it exists
    if ((this as any).teleportArrow) {
      ((this as any).teleportArrow as any).destroy();
      (this as any).teleportArrow = undefined;
    }

    // Destroy kill message if it exists
    if ((this as any).killMessage) {
      ((this as any).killMessage as any).destroy();
      (this as any).killMessage = undefined;
    }

    // Despawn all placed stickers — server-broadcast, unlike time-based blood splatters
    this.stickerManager?.removeAll();

    // Clean up airstrike warning UI and its intervals
    if (this._airStrikeWarningUI) {
      const { timerText, warningText, takeCoverText, crosshair, crosshairUpdateInterval, timerInterval } = this._airStrikeWarningUI;
      if (timerInterval) clearInterval(timerInterval);
      if (crosshairUpdateInterval) clearInterval(crosshairUpdateInterval);
      if (timerText && timerText.active) timerText.destroy();
      if (warningText && warningText.active) warningText.destroy();
      if (takeCoverText && takeCoverText.active) takeCoverText.destroy();
      if (crosshair && crosshair.active) crosshair.destroy();
      this._airStrikeWarningUI = undefined;
    }
  }

  destroy() {
    // Clean up match timer
    if (this.matchTimerEvent) {
      this.matchTimerEvent.destroy();
      this.matchTimerEvent = undefined;
    }

    if (this.matchTimerText) {
      this.matchTimerText.destroy();
      this.matchTimerText = undefined;
    }

    // Clean up chest lock countdown
    if (this.chestLockEvent) {
      this.chestLockEvent.destroy();
      this.chestLockEvent = undefined;
    }

    // Stop airstrike warning sound if playing
    if (this.sound && this.sound.stopByKey) {
      this.sound.stopByKey("alarm");
    }

    // Clean up fog overlay via map handler
    if (this.mapHandler) {
      this.mapHandler.destroyFogOverlay();
    }

    // Clear all damage texts and status labels
    clearAllDamageTexts();

    // Ensure shutdown is called to clean up listeners
    this.shutdown();

    if (this.windowManager) {
      this.windowManager.destroy();
      this.windowManager = undefined;
    }
    if (this.damageFlash) {
      this.damageFlash.destroy();
    }
    if (this.meleeAttackSpriteManager) {
      this.meleeAttackSpriteManager.destroy();
    }
    if (this.throwableHandler) {
      this.throwableHandler.destroy();
    }
    if (this.audioManager) {
      this.audioManager.destroy();
    }
    if (this.player) {
      this.player.destroy();
      this.player = undefined;
    }
    Object.values(this.otherPlayers).forEach((op: Player) => {
      op.destroy();
    });
    this.otherPlayers = {};
    this.cursors = null;
    this.keys = null;
  }

  // Returns true if the player steps on a tiles-effect value TELEPORT_TILE_INDEX
  // Checks a larger detection radius around the player
  checkEffectTile(x: number, y: number): boolean {
    const effectLayer = this.mapHandler?.getLayer("tiles-effect");
    if (!effectLayer) return false;

    // Use a larger detection radius to check multiple tiles around player
    const detectionRadius = 12; // pixels - covers approximately 1 tile around player

    // Check center and nearby tiles
    const tilesToCheck = [
      { dx: 0, dy: 0 }, // center
      { dx: detectionRadius, dy: 0 }, // right
      { dx: -detectionRadius, dy: 0 }, // left
      { dx: 0, dy: detectionRadius }, // down
      { dx: 0, dy: -detectionRadius }, // up
      { dx: detectionRadius, dy: detectionRadius }, // bottom-right
      { dx: -detectionRadius, dy: detectionRadius }, // bottom-left
      { dx: detectionRadius, dy: -detectionRadius }, // top-right
      { dx: -detectionRadius, dy: -detectionRadius }, // top-left
    ];

    for (const offset of tilesToCheck) {
      const tileX = Math.floor((x + offset.dx) / effectLayer.tilemap.tileWidth);
      const tileY = Math.floor(
        (y + offset.dy) / effectLayer.tilemap.tileHeight,
      );
      const tile = effectLayer.getTileAt(tileX, tileY);
      if (tile !== null && TELEPORT_TILE_INDEX.includes(tile.index)) {
        return true;
      }
    }
    return false;
  }

  // Returns true if the given world (pixel) coordinates are blocked
  // Uses a radius-based approach to allow squeezing through tight spaces
  isBlocked(x: number, y: number): boolean {
    return this.mapHandler?.isBlocked(x, y) ?? false;
  }

  // Update the last movement direction (called from controls)
  public updateMovementDirection(dx: number, dy: number) {
    this.lastMovementDirection = { x: dx, y: dy };
    this.lastMovementTime = Date.now();
  }

  /**
   * Create a single explosion sprite at the given coordinates
   * Uses the same explosion animation as grenades
   */
  private createAirStrikeExplosion(x: number, y: number, scale?: number): void {
    // Create explosion sprite at the location
    const explosionSprite = this.add.sprite(x, y, "explosion");
    explosionSprite.setOrigin(0.5);

    // Use provided scale from server, or default to 1
    const explosionScale = scale || 1;
    explosionSprite.setScale(explosionScale);
    explosionSprite.setDepth(1001);

    // Make sure UI camera ignores this sprite
    if (this.uiCamera) {
      this.uiCamera.ignore(explosionSprite);
    }

    // Create or reuse explosion animation
    if (!this.anims.exists("explosion_anim")) {
      this.anims.create({
        key: "explosion_anim",
        frames: this.anims.generateFrameNumbers("explosion", {
          start: 0,
          end: 8,
        }),
        frameRate: 15,
        repeat: 0,
      });
    }

    // Play explosion animation
    explosionSprite.play("explosion_anim");

    // Remove sprite after animation completes
    explosionSprite.on("animationcomplete", () => {
      explosionSprite.destroy();
    });
  }

  private createReconnectingOverlay(): void {
    // Create container for reconnecting overlay
    this.reconnectingOverlay = this.add.container(0, 0);
    this.reconnectingOverlay.setDepth(200000); // Above everything
    this.reconnectingOverlay.setVisible(false);

    // Semi-transparent black background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.7);
    bg.fillRect(0, 0, this.scale.width, this.scale.height);
    this.reconnectingOverlay.add(bg);

    // Create spinner (rotating circle)
    this.reconnectingSpinner = this.add.graphics();
    this.reconnectingSpinner.lineStyle(4, 0xffffff, 1);
    this.reconnectingSpinner.beginPath();
    this.reconnectingSpinner.arc(0, 0, 30, 0, Math.PI * 1.5, false);
    this.reconnectingSpinner.strokePath();
    this.reconnectingSpinner.setPosition(
      this.scale.width / 2,
      this.scale.height / 2 - 30,
    );
    this.reconnectingOverlay.add(this.reconnectingSpinner);

    // Reconnecting text
    this.reconnectingText = this.add
      .text(
        this.scale.width / 2,
        this.scale.height / 2 + 30,
        "Reconnecting...",
        {
          fontSize: "24px",
          color: "#ffffff",
          fontFamily: "Anton",
          align: "center",
        },
      )
      .setOrigin(0.5, 0.5);
    this.reconnectingOverlay.add(this.reconnectingText);

    // Make UI camera ignore main camera objects
    if (this.cameras.main) {
      this.cameras.main.ignore(this.reconnectingOverlay);
    }
  }

  public showReconnectingOverlay(): void {
    if (this.reconnectingOverlay) {
      this.reconnectingOverlay.setVisible(true);
    }
  }

  public hideReconnectingOverlay(): void {
    if (this.reconnectingOverlay) {
      this.reconnectingOverlay.setVisible(false);
    }
  }

  /**
   * Animate airplane shadow flying across the top of screen toward air drop position
   */
  private animateAirplane(dropPosition: { x: number; y: number }): void {
    if (!this.player) return;

    // Get player tile position
    const playerTileX = Math.floor(this.player.x / TILE_SIZE);
    const playerTileY = Math.floor(this.player.y / TILE_SIZE);

    // Calculate direction from player to drop position
    const dx = dropPosition.x - playerTileX;
    const dy = dropPosition.y - playerTileY;

    // Calculate angle to drop position
    const angle = Math.atan2(dy, dx);

    // Determine camera view
    const camera = this.cameras.main;
    const cameraWorldView = camera.worldView;

    // Calculate start position - opposite side of screen from drop direction
    // Add extra distance so airplane starts off-screen
    const startDistance = 600; // Increased from 300 to ensure fully off-screen
    const startX =
      cameraWorldView.x +
      cameraWorldView.width / 2 -
      Math.cos(angle) * startDistance;
    const startY =
      cameraWorldView.y +
      cameraWorldView.height / 2 -
      Math.sin(angle) * startDistance;

    // Calculate end position - fly through center toward drop direction
    // Increase end distance so airplane fully exits screen before being destroyed
    const endDistance = 600; // Increased from 300 to ensure fully off-screen
    const endX =
      cameraWorldView.x +
      cameraWorldView.width / 2 +
      Math.cos(angle) * endDistance;
    const endY =
      cameraWorldView.y +
      cameraWorldView.height / 2 +
      Math.sin(angle) * endDistance;

    // Create airplane sprite
    const airplane = this.add.image(startX, startY, "airplane-shadow");
    airplane.setDepth(10000);
    airplane.setScale(2); // Make it visible
    airplane.setScrollFactor(1); // Move with camera

    // Rotate airplane to point in direction of movement
    // Add 90 degrees (PI/2) because sprite is pointing up by default
    airplane.setRotation(angle + Math.PI / 2);

    // Make airplane only visible to main camera, not UI camera
    if (this.uiCamera) {
      this.uiCamera.ignore(airplane);
    }

    // Animate airplane across screen
    this.tweens.add({
      targets: airplane,
      x: endX,
      y: endY,
      duration: 2000,
      ease: "Linear",
      onComplete: () => {
        airplane.destroy();
      },
    });

    playSound(this, "airplane");
  }
}
